/**
 * Appends the Google Analytics cross-domain linker parameter (_gl) to a URL
 * This enables GA4 to track users across domains while maintaining session continuity
 *
 * Note: This function is necessary for programmatic redirects (window.location.href).
 * GTM's automatic link decoration only works for user-initiated clicks on <a> tags,
 * not for JavaScript redirects.
 *
 * @param {string} url - The URL to append the linker parameter to
 * @returns {string} - The URL with the _gl parameter appended (if available)
 */
export const appendGoogleLinkerParam = (url) => {
    if (!url || typeof window === 'undefined') {
        return url;
    }

    try {
        const urlObj = new URL(url);

        // If _gl parameter already exists, return the URL as-is
        if (urlObj.searchParams.has('_gl')) {
            return url;
        }

        let glParam = null;

        // Method 1: Try to extract from current URL if present
        // This ensures continuity if user arrived with a _gl parameter
        if (typeof window.location !== 'undefined') {
            const currentUrl = new URL(window.location.href);
            const currentGlParam = currentUrl.searchParams.get('_gl');
            if (currentGlParam) {
                glParam = currentGlParam;
            }
        }

        // Method 2: Construct from GA4 cookie as fallback
        if (!glParam && typeof document !== 'undefined') {
            const gaCookie = document.cookie
                .split('; ')
                .find((row) => row.startsWith('_ga='))
                ?.split('=')[1];

            if (gaCookie) {
                // GA cookie format: GA1.{version}.{random}.{timestamp}
                // Client ID is the last two parts, regardless of version (GA1.1, GA1.2, GA1.3, etc.)
                const parts = gaCookie.split('.');
                if (parts.length >= 4) {
                    // Use slice(-2) to grab last two segments, more robust across GA4 implementations
                    const clientId = parts.slice(-2).join('.');
                    const timestamp = Date.now();
                    // Basic _gl format: 1*{timestamp}*{clientId}
                    // Note: GTM's automatic decoration creates a more complex format
                    glParam = `1*${timestamp}*${clientId}`;
                }
            }
        }

        // Append the linker parameter if we found or constructed one
        if (glParam) {
            urlObj.searchParams.append('_gl', glParam);
            return urlObj.toString();
        }

        // If no method worked, return original URL
        return url;
    } catch (error) {
        console.error('Error appending Google linker param:', error);
        return url;
    }
};
